/**
 * Trial Access Control Utilities
 * 
 * Defines what trial users CAN and CANNOT do.
 * Used to gate features and show upgrade prompts.
 */

// Trial users CAN access these features
export const TRIAL_ALLOWED_FEATURES = [
  'create_business_plan',
  'define_offer',
  'business_builder_bot',
  'money_bot_basic',
  'daily_action_steps',
  'generate_basic_text',
  'view_dashboard',
  'onboarding',
  'run_my_week_basic',
  'quick_post_basic',
  'content_generation_basic',
];

// Trial users CANNOT access these features (Pro/Paid only)
export const PAID_ONLY_FEATURES = [
  'download_pdf',
  'download_html',
  'download_any',
  'export_content',
  'access_templates',
  'done_for_you_assets',
  'advanced_analytics',
  'performance_tracking',
  'unlimited_generation',
  'growth_tools',
  'business_brain_advanced',
  'momentum_tracking',
  'avatar_videos',
];

// GROWTH-tier features (Growth and Pro users)
export const GROWTH_FEATURES = [
  'canva_templates',
  'editable_design_templates',
  'template_download_links',
  'pexels_images',
  'assembly_instructions',
];

// PRO-tier exclusive features (Pro users only)
export const PRO_FEATURES = [
  'bundled_business_package',
  'fully_assembled_assets',
  'zip_downloads',
  'business_package_zip',
  'deploy_ready_outputs',
];

// Plan hierarchy for access checks
export const PLAN_HIERARCHY = {
  'trial': 0,
  'free': 0,
  'trialing': 0,
  'starter': 1,
  'growth': 2,
  'pro': 3,
  'internal': 3,
  'elite': 3,
};

/**
 * Check if user is on trial (not a paid subscriber)
 * 
 * IMPORTANT: When simulation_active is true, we ALWAYS use the simulated_plan_tier
 * and IGNORE any internal/admin bypasses. This allows admins to authentically test
 * the UI restrictions that regular users would see.
 */
export function isTrialUser(user) {
  if (!user) return true;
  
  // SIMULATION OVERRIDE: When simulating, ALWAYS use simulated tier
  // This overrides internal/admin bypasses to allow authentic testing
  if (user.simulation_active && user.simulated_plan_tier) {
    return ['trial', 'free', 'trialing'].includes(user.simulated_plan_tier.toLowerCase());
  }
  
  // Normal flow (not simulating)
  const planTier = user.plan_tier || user.planTier || 'trial';
  const subscriptionStatus = user.subscription_status || user.subscriptionStatus;
  const isInternal = user.is_internal || user.isInternal || planTier === 'internal';
  
  // Internal users are treated as Pro (only when NOT simulating)
  if (isInternal) return false;
  
  // Check subscription status
  if (subscriptionStatus === 'active') return false;
  
  // Check plan tier
  return ['trial', 'free', 'trialing'].includes(planTier.toLowerCase());
}

/**
 * Check if user has access to a specific feature
 * 
 * IMPORTANT: When simulation_active is true, we ALWAYS use the simulated_plan_tier
 * and IGNORE any internal/admin bypasses. This allows admins to authentically test
 * the UI restrictions that regular users would see.
 */
export function hasFeatureAccess(user, feature) {
  if (!user) return false;
  
  // SIMULATION OVERRIDE: When simulating, ALWAYS use simulated tier
  // This overrides internal/admin bypasses to allow authentic testing
  if (user.simulation_active && user.simulated_plan_tier) {
    const simulatedTier = user.simulated_plan_tier.toLowerCase();
    
    // Check if feature is in paid-only list
    if (PAID_ONLY_FEATURES.includes(feature)) {
      return PLAN_HIERARCHY[simulatedTier] >= 1;
    }
    
    // Trial-allowed features are available to everyone
    if (TRIAL_ALLOWED_FEATURES.includes(feature)) {
      return true;
    }
    
    // Default: check plan hierarchy
    return PLAN_HIERARCHY[simulatedTier] >= 1;
  }
  
  // Normal flow (not simulating)
  const planTier = user.plan_tier || user.planTier || 'trial';
  const isInternal = user.is_internal || user.isInternal || planTier === 'internal';
  
  // Internal users have full access (only when NOT simulating)
  if (isInternal) return true;
  
  // Check if feature is in paid-only list
  if (PAID_ONLY_FEATURES.includes(feature)) {
    // Only paid plans can access
    return PLAN_HIERARCHY[planTier.toLowerCase()] >= 1;
  }
  
  // Trial-allowed features are available to everyone
  if (TRIAL_ALLOWED_FEATURES.includes(feature)) {
    return true;
  }
  
  // Default: check plan hierarchy
  return PLAN_HIERARCHY[planTier.toLowerCase()] >= 1;
}

/**
 * Check if user can download/export content
 */
export function canDownload(user) {
  return hasFeatureAccess(user, 'download_any');
}

/**
 * Check if user can access templates
 */
export function canAccessTemplates(user) {
  return hasFeatureAccess(user, 'access_templates');
}

/**
 * Check if user can access done-for-you assets
 */
export function canAccessDoneForYou(user) {
  return hasFeatureAccess(user, 'done_for_you_assets');
}

/**
 * Check if user can access Canva templates (Growth+ tier)
 */
export function canAccessCanvaTemplates(user) {
  const tier = getEffectiveTier(user);
  return PLAN_HIERARCHY[tier] >= PLAN_HIERARCHY['growth'];
}

/**
 * Check if user can generate business package (Pro tier only)
 */
export function canGenerateBusinessPackage(user) {
  const tier = getEffectiveTier(user);
  return PLAN_HIERARCHY[tier] >= PLAN_HIERARCHY['pro'];
}

/**
 * Get allowed actions based on user's plan tier
 */
export function getAllowedActions(user) {
  const tier = getEffectiveTier(user);
  const tierLevel = PLAN_HIERARCHY[tier] || 0;
  
  return {
    copy: true,
    download_pdf: tierLevel >= 1,
    download_text: tierLevel >= 1,
    save_to_creations: tierLevel >= 1,
    open_template: tierLevel >= 2, // Growth+
    download_template_pack: tierLevel >= 2, // Growth+
    download_finished_asset: tierLevel >= 3, // Pro only
    download_business_package: tierLevel >= 3, // Pro only
  };
}

/**
 * Get the effective plan tier for a user
 * 
 * IMPORTANT: When simulation_active is true, we ALWAYS return the simulated_plan_tier
 * and IGNORE any internal/admin status. This allows admins to authentically test
 * the UI behaviors that regular users would see.
 */
export function getEffectiveTier(user) {
  if (!user) return 'trial';
  
  // SIMULATION OVERRIDE: When simulating, ALWAYS use simulated tier
  // This overrides internal/admin status to allow authentic testing
  if (user.simulation_active && user.simulated_plan_tier) {
    return user.simulated_plan_tier.toLowerCase();
  }
  
  // Normal flow (not simulating)
  const planTier = user.plan_tier || user.planTier || 'trial';
  const isInternal = user.is_internal || user.isInternal || planTier === 'internal';
  const role = user.role;
  
  // Internal users and admins are treated as Pro (only when NOT simulating)
  if (isInternal || role === 'admin') return 'pro';
  
  return planTier.toLowerCase();
}

/**
 * Get upgrade message based on what user tried to access
 */
export function getUpgradeMessage(feature) {
  const messages = {
    'download_pdf': 'Upgrade to download PDFs and finished assets.',
    'download_html': 'Upgrade to download and export your content.',
    'download_any': 'Upgrade to continue building your business.',
    'export_content': 'Upgrade to export your content.',
    'access_templates': 'Upgrade to unlock templates and downloads.',
    'done_for_you_assets': 'Upgrade to get done-for-you business assets.',
    'advanced_analytics': 'Upgrade to access advanced analytics.',
    'performance_tracking': 'Upgrade to track your performance.',
    'unlimited_generation': 'Upgrade for unlimited content generation.',
    'growth_tools': 'Upgrade to access growth tools.',
    'avatar_videos': 'Upgrade to create avatar videos.',
  };
  
  return messages[feature] || 'Upgrade to continue building your business.';
}

/**
 * Check if delivery mode requires upgrade for trial users
 */
export function requiresUpgradeForDeliveryMode(user, deliveryMode) {
  if (!isTrialUser(user)) return false;
  
  // Trial users can only get text_diy mode
  return deliveryMode !== 'text_diy';
}

/**
 * Get what trial users get vs paid users
 */
export function getTrialVsPaidComparison() {
  return {
    trial: {
      name: 'Trial',
      canDo: [
        'Create a business plan',
        'Define your offer',
        'Use Business Builder Bot',
        'See daily action steps',
        'Generate basic text outputs',
      ],
      cannotDo: [
        'Download files',
        'Access templates',
        'Get done-for-you assets',
        'Export content',
        'Use advanced analytics',
      ],
    },
    paid: {
      name: 'Paid Plans',
      features: [
        'Download PDFs and files',
        'Access editable templates',
        'Get done-for-you assets',
        'Export all content',
        'Advanced analytics & tracking',
        'Unlimited content generation',
      ],
    },
  };
}
